'use client';

import { useEffect, useState } from 'react';
import { canvasSafe } from '@/components/create-event/brand-import';
import { parseColour } from '@/lib/public/theme-colours';
import { analysisSize, densityFactor, logoMask, placedMarkBox, watermarkOpacity, WATERMARK_MIN_OPACITY, type LogoMask } from '@/lib/public/logo-watermark';
import type { WatermarkPlacement } from '@/lib/public/effects/catalogue';

/**
 * The "Logo watermark" background effect, and the tile that picks it.
 *
 * The logo is read once on a canvas (through our image proxy when it lives on
 * another host, so the canvas isn't tainted), reduced to a silhouette and
 * painted in one colour. The page then shows a single still image: no blur,
 * no animation beyond a short fade in, which reduced motion turns off.
 * If the logo can't be read, nothing is drawn and the page is plain.
 */

const masks = new Map<string, Promise<LogoMask | null>>();
const painted = new Map<string, string>();

function readMask(url: string): Promise<LogoMask | null> {
  let job = masks.get(url);
  if (!job) {
    job = new Promise<LogoMask | null>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = () => {
        try {
          const { width, height } = analysisSize(img.naturalWidth, img.naturalHeight);
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0, width, height);
          resolve(logoMask(ctx.getImageData(0, 0, width, height).data, width, height));
        } catch {
          // A tainted canvas or a broken file: no watermark, a plain page.
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = canvasSafe(url);
    });
    masks.set(url, job);
  }
  return job;
}

function paint(mask: LogoMask, tint: string): string | null {
  const rgb = parseColour(tint);
  if (!rgb) return null;
  const canvas = document.createElement('canvas');
  canvas.width = mask.width;
  canvas.height = mask.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const out = ctx.createImageData(mask.width, mask.height);
  for (let p = 0; p < mask.alpha.length; p++) {
    out.data[p * 4] = rgb[0];
    out.data[p * 4 + 1] = rgb[1];
    out.data[p * 4 + 2] = rgb[2];
    out.data[p * 4 + 3] = mask.alpha[p];
  }
  ctx.putImageData(out, 0, 0);
  return canvas.toDataURL('image/png');
}

/** The logo as a one-colour image in `tint`, with its shape; null while reading or when it can't be used. */
export function useLogoSilhouette(logoUrl: string | null | undefined, tint: string): { src: string; aspect: number; coverage: number } | null {
  const [state, setState] = useState<{ key: string; src: string; aspect: number; coverage: number } | null>(null);
  const key = logoUrl ? `${logoUrl}|${tint}` : '';

  useEffect(() => {
    if (!logoUrl) return;
    let live = true;
    readMask(logoUrl).then((mask) => {
      if (!live || !mask) {
        if (live) setState(null);
        return;
      }
      let src = painted.get(key);
      if (!src) {
        src = paint(mask, tint) ?? undefined;
        if (src) painted.set(key, src);
      }
      setState(src ? { key, src, aspect: mask.width / mask.height, coverage: mask.coverage } : null);
    });
    return () => {
      live = false;
    };
  }, [logoUrl, tint, key]);

  return state && state.key === key ? { src: state.src, aspect: state.aspect, coverage: state.coverage } : null;
}

/** The effect layer: one big, faint mark at the left, centre or right of the page. */
export function LogoWatermarkLayer({ logoUrl, page, tint, placement = 'right' }: { logoUrl?: string | null; page: string; tint: string; placement?: WatermarkPlacement }) {
  const mark = useLogoSilhouette(logoUrl, tint);
  const base = watermarkOpacity(page, tint);
  if (!mark || base === 0) return null;
  const box = placedMarkBox(mark.aspect, placement);
  // Centred, it sits behind the text, so it may go fainter than the side floor.
  const floor = placement === 'centre' ? WATERMARK_MIN_OPACITY * box.opacityFactor : WATERMARK_MIN_OPACITY;
  const opacity = Math.max(floor, base * densityFactor(mark.coverage) * box.opacityFactor);
  return (
    <div
      className="v2-fx-motion v2-fx-fade-in"
      data-effect="logo-watermark"
      data-placement={placement}
      style={{
        position: 'absolute',
        top: '50%',
        left: box.left,
        right: box.right,
        width: box.width,
        aspectRatio: box.aspectRatio,
        transform: box.transform,
        backgroundImage: `url("${mark.src}")`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        opacity,
      }}
    />
  );
}

/** The watermark as a picker tile draws it: the mark at the chosen side of a small box. */
export function LogoWatermarkMini({ logoUrl, ink, placement = 'right' }: { logoUrl?: string | null; ink: string; placement?: WatermarkPlacement }) {
  const mark = useLogoSilhouette(logoUrl, ink);
  if (!mark) return null;
  const side = placement === 'left' ? { left: -8 } : placement === 'centre' ? { left: '15%' } : { right: -8 };
  return (
    <div
      style={{
        position: 'absolute',
        top: 4,
        bottom: 4,
        width: '70%',
        ...side,
        backgroundImage: `url("${mark.src}")`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: placement === 'left' ? 'left center' : placement === 'centre' ? 'center' : 'right center',
        // Stronger than on the page, so the tile shows what it is.
        opacity: placement === 'centre' ? 0.22 : 0.3,
      }}
    />
  );
}

/**
 * The picker tile for Branding's background effects, in the v8 look or the
 * older one. With no logo it is shown but can't be chosen, and says why.
 */
export function LogoWatermarkEffectTile({
  v8,
  on,
  onSelect,
  logoUrl,
  background,
  ink,
}: {
  v8?: boolean;
  on: boolean;
  onSelect: () => void;
  logoUrl?: string | null;
  background: string;
  ink: string;
}) {
  const hasLogo = !!logoUrl;
  const mark = useLogoSilhouette(logoUrl, ink);
  const swatch = (
    <div
      style={{
        height: v8 ? 44 : 48,
        borderRadius: v8 ? 0 : 6,
        marginBottom: v8 ? 0 : 8,
        overflow: 'hidden',
        position: 'relative',
        background,
        border: v8 ? 'none' : '1px solid var(--fv-border)',
      }}
    >
      {hasLogo && mark && (
        <div
          style={{
            position: 'absolute',
            top: 4,
            bottom: 4,
            right: -8,
            width: '70%',
            backgroundImage: `url("${mark.src}")`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right center',
            // Stronger than on the page, so the tile shows what it is.
            opacity: 0.3,
          }}
        />
      )}
    </div>
  );
  const hint = hasLogo ? null : 'Add a logo first';

  if (v8) {
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        aria-disabled={!hasLogo || undefined}
        disabled={!hasLogo && !on}
        onClick={() => { if (hasLogo) onSelect(); }}
        className={on || !hasLogo ? undefined : 'fvd-hoverable'}
        style={{
          padding: 0,
          border: `1px solid ${on ? 'var(--fvd-ink)' : 'var(--fvd-hairline-strong)'}`,
          borderRadius: 9,
          overflow: 'hidden',
          background: 'var(--fvd-canvas)',
          cursor: hasLogo ? 'pointer' : 'not-allowed',
          textAlign: 'left',
        }}
      >
        {swatch}
        <span style={{ display: 'block', padding: '6px 8px', fontSize: 11.5, fontWeight: on ? 600 : 450, color: hasLogo ? 'var(--fvd-ink)' : 'var(--fvd-ink-muted)' }}>
          Logo watermark
          {hint && <span style={{ display: 'block', fontSize: 11, fontWeight: 450, color: 'var(--fvd-ink-muted)', marginTop: 1 }}>{hint}</span>}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={!hasLogo && !on}
      onClick={() => { if (hasLogo) onSelect(); }}
      style={{
        borderRadius: 8,
        padding: 12,
        textAlign: 'left',
        transition: 'all 0.15s',
        cursor: hasLogo ? 'pointer' : 'not-allowed',
        border: on ? '2px solid var(--fv-accent)' : '1px solid var(--fv-border)',
        background: 'var(--fv-bg-card)',
        opacity: hasLogo ? 1 : 0.6,
      }}
    >
      {swatch}
      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--fv-text-primary)', fontFamily: 'var(--font-instrument-sans)', marginBottom: 1 }}>Logo watermark</div>
      <div style={{ fontSize: 11, color: 'var(--fv-text-tertiary)', fontFamily: 'var(--font-instrument-sans)' }}>{hint ?? 'Your logo, large and faint'}</div>
    </button>
  );
}
